import React, { useEffect, useState } from "react"
import PropTypes from "prop-types";
import ProjectOverviewTable from "../ProjectOverviewTable";
import HeatMapByPartner from "../../Charts/HeatMapByPartner";
import { Divider } from "@mui/material";
import { getApi } from "../../../../utils/services";
import { JOURNEY_TO_APPRENTICE_BY_PARTNER_LIST, PROJECT_OVERVIEW_LIST } from "../../../../utils/services/apiPath";
import { errorToast } from "../../../../utils/toastHelper";
import dayjs from "dayjs";
import { dummyData } from './DummyData';
import EmptyStateOverlay from '../EmptyStateOverlay/EmptyStateOverlay';

export default function DashboardHeatMap({ setHideHeatMap, selectedProjectsQuery, startLoading, stopLoading }) {
  const [heatMapData, setHeatMapData] = useState();
  const [selectedDate, setSelectedDate] = useState(null);
  const [projectOverviewData, setProjectOverviewData] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);

  const getHeatMapData = async () => {
    startLoading('heatMap');
    try {
      const data = await getApi(`${JOURNEY_TO_APPRENTICE_BY_PARTNER_LIST}?projectIds=${selectedProjectsQuery}`);
      if (data.error) return;
      const apprenticeJourneyData = data.map(({ companyName, data: mapInfo }) => {
        return { companyName, data: mapInfo.slice(-12) };
      });
      const dates = [];
      apprenticeJourneyData.forEach(item => {
        item.data.forEach(dataItem => {
          if (!dates.includes(dataItem.weekEndingDate)) dates.push(dataItem.weekEndingDate);
        });
      });
      dates.sort((a, b) => dayjs(a, "YYYY-MM-DD").unix() - dayjs(b, "YYYY-MM-DD").unix());
      const seriesData = [];
      const companyName = [];
      if (apprenticeJourneyData[apprenticeJourneyData.length - 1]?.data[0]) {
        const firstWeekendingDate = apprenticeJourneyData[apprenticeJourneyData.length - 1].data[0].weekEndingDate;
        setSelectedDate(firstWeekendingDate);
      }
      apprenticeJourneyData.forEach((item, index) => {
        companyName.push(item.companyName);
        item.data.forEach(dataItem => {
          const dateIndex = dates.indexOf(dataItem.weekEndingDate);
          seriesData.push({
            x: dateIndex,
            y: index,
            value: +dataItem.journeyToApprenticeRatio,
            date: dataItem.weekEndingDate,
            clientId: dataItem.globalClientGuid
          });
        });
      });
      setHideHeatMap(apprenticeJourneyData.length === 0);
      setHeatMapData({ companyName, dates, formattedDates: dates.map(date => dayjs(date, "YYYY-MM-DD").format("DD MMM")), seriesData });
    } catch (error) {
      errorToast(error);
    } finally {
      stopLoading('heatMap');
    }
  };

  const getProjectOverviewData = async () => {
    startLoading('projectOverview');
    try {
      const data = await getApi(PROJECT_OVERVIEW_LIST, { params: { weekEndingDate: selectedDate, globalClientGuid: selectedClient, projectIds: selectedProjectsQuery } });
      setProjectOverviewData(data.map(item => ({ ...item, isDummy: !item.projectName })).filter((item, i) => i < 5 || !item.isDummy));
    } catch (error) {
      errorToast(error);
    } finally {
      stopLoading('projectOverview');
    }
  };

  const onPointSelect = event => {
    setSelectedClient(event.clientId);
    setSelectedDate(event.date);
  };

  useEffect(() => {
    getHeatMapData();
  }, [selectedProjectsQuery]);

  useEffect(() => {
    // Allow getProjectOverviewData to run without a selectedClient (undefined will fetch current user's client data)
    // If no selectedClient, the API gets data for the current user's client data
    if (selectedDate) getProjectOverviewData();
  }, [selectedDate, selectedClient, selectedProjectsQuery]);

  const hasData = heatMapData?.companyName?.length > 0;
  const companyConfigurationmessage = "No projects configured yet. Add projects to generate the heat map";

  return (
    <div className="djr-Wrapper" style={{ margin: "0 auto 3px" }}>
      <div className="djr-header with-dropdown">
        <div className="djr-header-title-wrapper">
          <div className="djr-header-subtitle">Journey to Apprentice</div>
          <div className="djr-header-title">By Partner</div>
        </div>
      </div>
      <div className="djr-heat-map">
        <div style={{ position: 'relative', height: '100%' }}>
          <HeatMapByPartner heatMapData={!hasData ? dummyData : heatMapData} onPointSelect={onPointSelect} />
          {!hasData && <EmptyStateOverlay message={companyConfigurationmessage} backdropBlur="blur(2px)" />}
        </div>
      </div>
      <Divider />
      <ProjectOverviewTable
        projectOverviewData={projectOverviewData}
        loading={false}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />
    </div>
  );
}

DashboardHeatMap.propTypes = {
  setHideHeatMap: PropTypes.func.isRequired,
};